import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';

function App() {
  const [title, setTitle] = useState('Quiz Title Missing');
  const [answers, setAnswers] = useState({});
  const [rightAnswer, setRightAnswer] = useState('');
  const [domain, setDomain] = useState('http://localhost:3900');
  const [percentages, setPercentages] = useState({});
  const [isShowingPollResults, setIsShowingPollResults] = useState(false);
  const [isShowingPollAnswer, setIsShowingPollAnswer] = useState(false);
  const [isShowingWinner, setIsShowingWinner] = useState(false);
  const [winner, setWinner] = useState(null);
  const [winnerAvatar, setWinnerAvatar] = useState('');
  const [roomID, setRoomID] = useState('');
  const [hasShownAnswer, setHasShownAnswer] = useState(false);
  const [hasShownWinner, setHasShownWinner] = useState(false);

  // ── Refs so async callbacks always read the latest values without
  //    being listed as useEffect dependencies (avoids socket reconnects)
  const winnerRef = useRef(null);
  const rightAnswerRef = useRef('');
  const isShowingWinnerRef = useRef(false);
  const hasShownWinnerRef = useRef(false);
  const hasSavedPoll = useRef(false);

  // Keep refs in sync with state
  // winnerRef is updated synchronously inside processMessage (not via useEffect)
  // to prevent a race where two correct answers arrive before the async effect fires.
  useEffect(() => { rightAnswerRef.current = rightAnswer; }, [rightAnswer]);
  useEffect(() => { isShowingWinnerRef.current = isShowingWinner; }, [isShowingWinner]);
  useEffect(() => { hasShownWinnerRef.current = hasShownWinner; }, [hasShownWinner]);

  // ── Parse URL parameters ─────────────────────────────────────────────────
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.has('domain')) {
      setDomain(urlParams.get('domain'));
    }

    const urlTitle = urlParams.get('title');
    if (urlTitle) {
      try {
        let decodedTitle = urlTitle.replace(/%(?![\da-f]{2})/gi, '%25');
        decodedTitle = decodeURIComponent(decodedTitle);
        setTitle(decodedTitle);
      } catch (e) {
        console.error('Failed to decode title:', e);
        setTitle(urlTitle);
      }
    }

    const right = urlParams.get('rightAnswer');
    if (right) {
      setRightAnswer(right);
    }

    const lettersToCheck = ['a', 'b', 'c', 'd', 'e', 'f'];
    const answerObj = {};
    lettersToCheck.forEach(letter => {
      const answerText = urlParams.get(letter);
      if (answerText && answerText !== '') {
        try {
          let decodedText = answerText.replace(/%(?![\da-f]{2})/gi, '%25');
          decodedText = decodeURIComponent(decodedText);
          answerObj[letter] = decodedText;
        } catch (e) {
          console.error('Failed to decode:', e);
          answerObj[letter] = answerText;
        }
      }
    });
    setAnswers(answerObj);

    const saveToParam = urlParams.get('saveto');
    if (saveToParam) {
      // saveto IS the overlay.ninja room ID — same value t.py hardcodes
      console.log('saveto parameter found:', saveToParam, '→ using as WebSocket room ID');
      setRoomID(saveToParam);
      if (!hasSavedPoll.current) {
        checkUserAndSavePoll(saveToParam, urlTitle);
      }
    } else {
      console.warn('⚠️ No saveto parameter found — WebSocket will not connect until server provides roomId');
    }

    fetch(`${domain}/clear-chat?words=a,b,c,d,e,f`).catch(error =>
      console.error('Error clearing chat:', error)
    );
  }, []);

  // ── Supabase: check user & save poll ────────────────────────────────────
  const checkUserAndSavePoll = async (userId, pollTitle) => {
    if (hasSavedPoll.current) {
      console.log('Poll already saved, skipping duplicate save');
      return;
    }
    hasSavedPoll.current = true;

    try {
      console.log('Checking user:', userId);
      const { data: userData, error: userError } = await supabase
        .from('message_users')
        .select('*')
        .eq('user_id', userId)
        .eq('enabled', true)
        .maybeSingle();

      if (userError) {
        console.error('Error checking user:', userError);
        return;
      }

      if (userData) {
        console.log('User found and enabled:', userData);
        let decodedTitle = '';
        if (pollTitle) {
          try {
            let encodedTitle = pollTitle.replace(/%(?![\da-f]{2})/gi, '%25');
            decodedTitle = decodeURIComponent(encodedTitle);
          } catch (e) {
            console.error('Failed to decode poll title:', e);
            decodedTitle = pollTitle;
          }
        }
        console.log('Saving poll with title:', decodedTitle);
        const { error: saveError } = await supabase
          .from('message_registry')
          .insert({
            user_id: userId,
            name: 'Poll Question',
            message: decodedTitle
          });

        if (saveError) {
          console.error('Error saving poll:', saveError);
        } else {
          console.log('Poll question saved successfully');
        }
      } else {
        console.log('User not found or not enabled');
      }
    } catch (error) {
      console.error('Error in Supabase operation:', error);
      hasSavedPoll.current = false;
    }
  };

  // ── Spring-physics progress bar animation ───────────────────────────────
  const animateProgress = useCallback((answer, newPercentage) => {
    console.log(`Animating progress for answer ${answer} to ${newPercentage}%`);
    const progressBar = document.querySelector(`.progress-bar-${answer}`);
    const percentageDisplay = document.querySelector(`.percentage-${answer}`);

    if (!percentageDisplay) {
      console.warn(`Could not find percentage display for answer ${answer}`);
      return;
    }
    if (!progressBar) {
      console.warn(`Could not find progress bar for answer ${answer}`);
      return;
    }

    let currentPercentage = parseInt(percentageDisplay.textContent, 10);
    if (currentPercentage === newPercentage) return;

    const spring = { stiffness: 0.08, damping: 0.32, precision: 0.01 };
    let velocity = 0;
    let currentValue = currentPercentage;

    function springAnimation() {
      const distance = newPercentage - currentValue;
      const force = distance * spring.stiffness;
      velocity += force;
      velocity *= (1 - spring.damping);
      currentValue += velocity;

      progressBar.style.width = `${currentValue}%`;
      percentageDisplay.textContent = `${Math.round(currentValue)}%`;

      if (Math.abs(distance) > spring.precision || Math.abs(velocity) > spring.precision) {
        requestAnimationFrame(springAnimation);
      } else {
        progressBar.style.width = `${newPercentage}%`;
        percentageDisplay.textContent = `${newPercentage}%`;
      }
    }

    requestAnimationFrame(springAnimation);
  }, []);

  // ── Reveal correct answer highlight ─────────────────────────────────────
  const revealAnswer = useCallback(() => {
    const elementWithTrueData = document.querySelector('[data-is-right="true"]');
    if (elementWithTrueData) {
      const answerLetterElement = elementWithTrueData.querySelector('.answer-letter');
      if (answerLetterElement) {
        answerLetterElement.classList.add('correct-answer');
      }
      const progressBarElement = elementWithTrueData.querySelector('.progress-bar');
      if (progressBarElement) {
        progressBarElement.setAttribute('data-right', 'true');
      }
    }
    try {
      const audio = new Audio('/correct.mp3');
      audio.volume = 0.1;
      audio.play().catch(e => console.error('Error playing sound:', e));
    } catch (error) {
      console.error('Error with audio:', error);
    }
  }, []);

  // ── Reveal winner overlay ────────────────────────────────────────────────
  // NOTE: reads winner from state (called only from the dedicated effect below)
  const revealWinner = useCallback(() => {
    console.log('revealWinner called, winner:', winnerRef.current);
    if (!winnerRef.current) {
      console.log('No winner set, cannot reveal');
      return;
    }

    const winnerNameParent = document.getElementById("winner-name")?.parentElement;
    const winnerAvatarElement = document.getElementById("winner-avatar");
    const winnerBackgroundElement = document.querySelector(".winner-background");
    const quizTitleElement = document.getElementById("quiz-title");
    const answerContainerElement = document.getElementById("answer-container");

    winnerNameParent?.classList.remove('hidden');
    winnerAvatarElement?.classList.remove('hidden');
    winnerBackgroundElement?.classList.add('show');
    quizTitleElement?.classList.add('hidden');
    answerContainerElement?.classList.add('hidden');

    if (window.confetti) {
      window.confetti({ particleCount: 100, spread: 60, origin: { y: 0.6, x: 0.25 } });
    }

    setTimeout(() => {
      try {
        const audio = new Audio('/ta-da.mp3');
        audio.volume = 0.2;
        audio.play().catch(e => console.error('Error playing sound:', e));
      } catch (error) {
        console.error('Error with audio:', error);
      }
    }, 200);
  }, []); // no deps — reads winner via ref, never goes stale

  // ── FIX: Dedicated effect that triggers revealWinner as soon as BOTH
  //    winner AND isShowingWinner are true, regardless of which arrives first.
  //    This replaces the fragile timing-dependent check that was inside fetchData.
  useEffect(() => {
    if (winner && isShowingWinner && !hasShownWinner) {
      console.log('Winner + isShowingWinner both ready — revealing winner:', winner);
      revealWinner();
      setHasShownWinner(true);
    }
  }, [winner, isShowingWinner, hasShownWinner, revealWinner]);

  // ── Poll data fetch (500 ms interval) ───────────────────────────────────
  // winner is read via ref here so it is NOT a dependency → no effect restart
  useEffect(() => {
    const fetchData = () => {
      fetch(`${domain}/wordcloud`)
        .then(response => response.json())
        .then(data => {
          console.log('Received data from server:', data);

          const serverIsShowingPollResults = data.isShowingPollResults;
          const serverIsShowingPollAnswer = data.isShowingRightAnswer;
          const serverIsShowingWinner = data.isShowingWinner;
          // Only use server roomId as fallback if we don't already have one from URL
          if (data.roomId && !roomID) {
            console.log('Using server-provided roomId:', data.roomId);
            setRoomID(data.roomId);
          }

          setIsShowingPollResults(serverIsShowingPollResults);
          setIsShowingPollAnswer(serverIsShowingPollAnswer);
          setIsShowingWinner(serverIsShowingWinner);

          if (!serverIsShowingPollResults) {
            console.log('Poll results not showing yet');
            return;
          }

          console.log('Poll results should be showing');

          document.querySelectorAll('.percentage').forEach(el => {
            el.classList.add('opacity-100');
            el.classList.remove('opacity-0');
          });

          document.querySelectorAll('.progress-bar').forEach(el => {
            el.parentElement.classList.add('opacity-100');
            el.parentElement.classList.remove('opacity-0');
          });

          if (!hasShownAnswer && serverIsShowingPollAnswer) {
            console.log('Revealing right answer');
            revealAnswer();
            setHasShownAnswer(true);
          }

          // Winner reveal is now handled exclusively by the dedicated useEffect above.
          // We no longer call revealWinner() here to avoid the timing race.

          if (!data.wordcloud || typeof data.wordcloud !== 'string') {
            console.log('No wordcloud data to process');
            return;
          }

          console.log('Raw wordcloud data:', data.wordcloud);
          const letters = Object.keys(answers);
          const filteredArray = data.wordcloud.toLowerCase().split(',').filter(char => letters.includes(char));
          console.log('Filtered votes:', filteredArray);

          if (filteredArray.length === 0) {
            console.log('No votes found in chat data');
            return;
          }

          const countObj = filteredArray.reduce((acc, char) => {
            acc[char] = acc[char] ? acc[char] + 1 : 1;
            return acc;
          }, {});
          console.log('Vote counts:', countObj);

          const totalCount = Object.values(countObj).reduce((acc, value) => acc + value, 0);
          if (totalCount === 0) return;

          const percentageObj = {};
          Object.keys(countObj).forEach(key => {
            percentageObj[key] = Math.round((countObj[key] / totalCount) * 100);
            animateProgress(key, percentageObj[key]);
          });

          console.log('Final percentages:', percentageObj);
          setPercentages(percentageObj);
        })
        .catch(error => console.error('Error fetching data:', error));
    };

    fetchData();
    const interval = setInterval(fetchData, 500);
    return () => clearInterval(interval);
    // winner deliberately omitted — read via ref to avoid restarting the interval
  }, [domain, answers, animateProgress, revealAnswer, hasShownAnswer]);

  // ── WebSocket: incoming chat messages ────────────────────────────────────
  // winner and isShowingWinner are read via refs — NOT deps — so the socket
  // is never torn down and re-created just because a winner was found.
  useEffect(() => {
    const processMessage = (message) => {
      console.log("--- processMessage called ---");
      console.log('Processing raw message:', message);

      const chatMessage = message?.chatmessage || message?.message;
      console.log("Extracted chatMessage:", chatMessage);
      console.log("Target rightAnswer (ref):", rightAnswerRef.current);
      console.log("Current winner (ref):", winnerRef.current);

      if (!chatMessage) {
        console.log("No chat message extracted, ignoring.");
        return;
      }

      const cleanMessage = chatMessage.toString().trim().toLowerCase();
      const targetAnswer = rightAnswerRef.current.toString().trim().toLowerCase();
      
      console.log("Checking if", cleanMessage, "===", targetAnswer);

      if (cleanMessage === targetAnswer && !winnerRef.current) {
        const winnerName = message.chatname || message.name;
        console.log("Determined winner name:", winnerName);

        if (!winnerName) {
          console.log("Winner name could not be determined, ignoring.");
          return;
        }

        console.log(`${winnerName} is the WINNER!`);

        // Update ref SYNCHRONOUSLY before the async state update so that any
        // subsequent messages processed in the same tick see a non-null winner
        // and are correctly rejected by the !winnerRef.current guard above.
        winnerRef.current = winnerName;

        // Persist to state so the dedicated reveal effect can fire
        setWinner(winnerName);

        const avatar = message.chatimg || message.img;
        console.log("Determined avatar:", avatar);

        if (avatar) {
          setWinnerAvatar(avatar);
          const avatarElement = document.querySelector("#winner-avatar img");
          if (avatarElement) avatarElement.src = avatar;
        }

        // Write name to DOM immediately (state update is async)
        const nameElement = document.getElementById("winner-name");
        if (nameElement) nameElement.textContent = winnerName;

        fetch(`${domain}/replacePin?name=${encodeURIComponent(winnerName)}`, {
          method: "get",
          headers: { 'Content-Type': 'application/json' },
        }).then(r => r.text()).catch(e => console.error('Error replacing pin:', e));

        // If the server already has isShowingWinner = true, reveal immediately.
        // (The dedicated effect will also catch this, but this handles the case
        //  where isShowingWinner was already true before the winner arrived.)
        if (isShowingWinnerRef.current && !hasShownWinnerRef.current) {
          revealWinner();
          setHasShownWinner(true);
        }
      }
    };

    // ── window.postMessage handler (dock / iframe)
    const messageHandler = (e) => {
      try {
        if (e.data && typeof e.data === 'string') {
          try {
            if (!e.data.trim().startsWith('{') && !e.data.trim().startsWith('[')) return;
            const data = JSON.parse(e.data);
            if (data.type === 'chat') {
              processMessage({ chatmessage: data.message, chatname: data.name, chatimg: data.image });
            }
          } catch {
            console.debug('Received non-JSON message from window');
          }
        } else if (e.data && typeof e.data === 'object' && 'dataReceived' in e.data) {
          if (e.data.dataReceived?.overlayNinja) {
            processMessage(e.data.dataReceived.overlayNinja);
          }
        }
      } catch (error) {
        console.error('Error processing window message:', error);
      }
    };

    window.addEventListener('message', messageHandler);

    // ── WebSocket setup
    let socketserver = null;
    let isShuttingDown = false;
    let retryDelay = 1000;

    const connectWebSocket = () => {
      if (isShuttingDown) return;
      if (!roomID) {
        console.warn('⚠️ connectWebSocket called but roomID is empty — skipping');
        return;
      }

      console.log(`📡 Connecting to overlay.ninja WebSocket... (Room: ${roomID})`);
      const serverURL = "wss://api.overlay.ninja/api";
      socketserver = new WebSocket(serverURL);

      socketserver.onopen = () => {
        console.log(`✅ WebSocket Connected. Joining room ${roomID}`);
        retryDelay = 1000; // Reset delay on successful connection
        socketserver.send(JSON.stringify({ "join": roomID, "out": 2, "in": 1 }));
      };

      socketserver.onmessage = (event) => {
        if (!event.data) return;
        try {
          const outer = JSON.parse(event.data);
          
          // Debug everything except pings/pongs
          if (!outer.ping && !outer.pong) {
             console.log("📨 WebSocket Raw Msg:", outer);
          }

          if (outer.value) {
            let inner;
            try {
              inner = JSON.parse(outer.value);
            } catch (e) {
              processMessage(outer);
              return;
            }

            if (Array.isArray(inner) && inner.length > 0) {
              inner = inner[0];
            }

            if (inner && typeof inner === 'object' && inner.dataReceived) {
              const overlay = inner.dataReceived.overlayNinja || inner;
              processMessage(overlay);
            } else {
              processMessage(inner);
            }
          } else {
             // System messages & acks. Don't process them as chat messages 
             // because they don't contain chat data, exactly like python app.
          }
        } catch (error) {
          console.error("❌ Error parsing WebSocket message:", error);
        }
      };

      socketserver.onclose = () => {
        console.log(`🔌 WebSocket Closed. Reconnecting in ${retryDelay}ms...`);
        if (!isShuttingDown) {
          setTimeout(() => connectWebSocket(), retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000); // Backoff up to 30s
        }
      };

      socketserver.onerror = (err) => {
        console.error("❌ WebSocket Error:", err);
      };
    };

    connectWebSocket();

    return () => {
      isShuttingDown = true;
      window.removeEventListener('message', messageHandler);
      if (socketserver) {
        socketserver.onclose = null; // Prevent reconnect
        socketserver.close();
      }
    };
    // winner / isShowingWinner deliberately omitted — read via refs
    // so the socket is never needlessly reconnected
  }, [rightAnswer, roomID, domain, revealWinner]);

  // ── Sync percentages to DOM when state updates ───────────────────────────
  useEffect(() => {
    Object.entries(percentages).forEach(([letter, percent]) => {
      const progressBar = document.querySelector(`.progress-bar-${letter}`);
      const percentageDisplay = document.querySelector(`.percentage-${letter}`);
      if (progressBar && percentageDisplay) {
        progressBar.style.width = `${percent}%`;
        percentageDisplay.textContent = `${percent}%`;
      }
    });
  }, [percentages]);

  // ── Answer option renderer ───────────────────────────────────────────────
  const createAnswerOption = (letter) => {
    if (!answers[letter]) return null;
    const isRight = letter === rightAnswer;
    const percentageClass = `percentage text-white font-bold text-xl percentage-${letter} transition-opacity duration-500 ease-in-out opacity-0`;
    const progressBarClass = `progress-bar bg-blue-600 h-4 rounded progress-bar-${letter}`;

    return (
      <div key={letter} className="mb-8 w-1/2 px-2" data-is-right={isRight ? "true" : "false"}>
        <div className="flex justify-between items-center mb-1 gap-x-3">
          <div>
            <span className="answer-letter bg-blue-900 text-white text-xl font-medium me-2 px-2.5 py-0.5 rounded">
              {letter.toUpperCase()}
            </span>
            <span className="text-white text-xl font-bold">{answers[letter]}</span>
          </div>
          <span className={percentageClass}>0%</span>
        </div>
        <div className="h-4 bg-gray-300 rounded transition-opacity duration-500 ease-in-out opacity-0">
          <div className={progressBarClass} style={{ width: '0%' }}></div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full p-4 rounded-2xl mt-40 animated-gradient">
      <h1 id="quiz-title" className="text-white font-bold text-3xl mb-12 text-center">{title}</h1>
      <div id="answer-container" className="flex flex-wrap -mx-2">
        {Object.keys(answers).map(letter => createAnswerOption(letter))}
      </div>
      <div className="flex flex-col justify-center items-center h-full">
        <div className="winner-container">
          <div className="winner-background">
            <div className="winner-gif"></div>
            <div className="winner-gif-flipped"></div>
          </div>
          <div className="p-1 rounded-full shadow-lg hidden" id="winner-avatar">
            <img src={winnerAvatar} alt="Winner Avatar" />
          </div>
          <h1 className="text-white font-bold text-3xl text-center hidden">
            <span id="winner-name"></span>
          </h1>
        </div>
      </div>
    </div>
  );
}

export default App;